import { invalidateAbsences, useAbsenceStanding } from '@/api/absence';
/**
 * The one absence drawer, opened from Fravær and from Vaktplan. It was the Fravær
 * screen's inline drawer until 2026-09-18; nothing about it changed in the move, so
 * the two screens cannot answer differently. The API owns what deducts, how many days
 * a range costs and whether the end date counts: the amount comes from
 * GET /absence-requests/deduction, never from a sum here.
 *
 * Writes: POST, PUT and DELETE /admin/absence-requests are absence.manage;
 * PUT :id/approve and POST :id/reject are requests.decide.
 */
import { ApiError, errorText } from '@/api/client';
import {
  type ApiAbsence,
  type ApiAbsenceType,
  type ApiShift,
  useAbsenceLimits,
  useAbsenceTypes,
  useAbsenceWrites,
  useShiftWrites,
  useUsers,
  useWorkTimeChanges,
} from '@/api/data';
import { useCan } from '@/auth/caps';
import { T } from '@/i18n';
import { oslo, osloISO } from '@/rules';
import { askBeforeLeaving, useDirty } from '@/shell/context';
import { Button, Drawer, toast } from '@/ui';
import { avTone, fullName, initials, num } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { useQueryClient } from '@tanstack/react-query';
import { inRange, toUTC } from '@timefly/core';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import History from '../arbeidstid/History';
import {
  type Kind,
  type Range,
  RangeFields,
  bodyOf,
  describe,
  explain,
  kindOf,
  rangeOf,
  sameRange,
} from './AbsenceRange';
import { DeductionLine, deductionLine, useDeductionState } from './Deduction';
import { QuotaRow } from './QuotaRow';
import { lawNote, limitsText, ownVacationWithoutDeduction, payerText } from './catalogue';
import { dayText, periodText, stampText } from './dates';
import { StatusChip } from './parts';
import './fravaer.css';
import { useTenant } from '@/shell/tenant';
import { teamsOf } from './filters';

const dOf = (iso: string | null | undefined) => oslo(iso)?.date ?? '';
/** The end date is a day of absence (web-absence.md §4, ruling 2). */
export const daysOf = (from: string, to: string) =>
  Math.round((toUTC(to).getTime() - toUTC(from).getTime()) / 864e5) + 1;

export interface AbsenceDraft extends Range {
  id: number | null;
  user_id: number;
  absence_type_id: number;
  reason: string;
  /** Sykmeldingsgrad as typed, '' for a whole sick leave (ftrl §8-13, 2026-09-22) */
  grade: string;
  status: ApiAbsence['status'];
  /** the stored instants, sent back verbatim when the range was not touched (a reason-only edit) */
  orig: { start_date: string; end_date: string } | null;
  /** when the request was sent, for "Søknad mottatt" */
  created?: string | null;
}

/** An existing request as the drawer edits it. */
export const draftOfAbsence = (a: ApiAbsence): AbsenceDraft => ({
  id: a.id,
  user_id: a.user_id,
  absence_type_id: a.absence_type?.id ?? 0,
  ...rangeOf(a),
  reason: a.reason ?? '',
  grade: a.sick_grade == null ? '' : String(a.sick_grade),
  status: a.status,
  orig: { start_date: a.start_date, end_date: a.end_date },
  created: a.created_at ?? null,
});

/**
 * A new absence for one person on one day, with the type the caller picked. Left at 0 the
 * drawer fills it with the first active type once the list is there, so a caller that has
 * no reason to know the types does not have to.
 */
export const newAbsenceDraft = (user_id: number, date: string, absence_type_id = 0): AbsenceDraft => ({
  id: null,
  user_id,
  absence_type_id,
  from: date,
  fromT: '',
  to: date,
  toT: '',
  reason: '',
  grade: '',
  status: 'accepted',
  orig: null,
});

/**
 * Runs one write and says what happened. A 409 means the amount moved since it was
 * reviewed: the server charged nothing and names the actual count, so the request and
 * its amount are read again rather than approved blind.
 */
export function useAbsenceRun() {
  const qc = useQueryClient();
  return async (label: string, f: () => Promise<unknown>) => {
    try {
      await f();
      toast(label);
    } catch (e) {
      const days =
        e instanceof ApiError && e.status === 409
          ? (e.body as { days?: number } | undefined)?.days
          : undefined;
      if (days !== undefined) {
        void invalidateAbsences(qc);
        toast(
          T('Trekket er endret siden du så det: {count} dag(er). Se over og godkjenn på nytt.', {
            count: days,
          }),
        );
        return;
      }
      toast(errorText(e));
    }
  };
}

/**
 * The approval with the amount the manager saw (Codex resolution 2026-09-14): the line
 * from the server, the button held until it is known, expected_days sent so the server
 * refuses with the actual count if it changed since review. A type that does not deduct
 * shows no line and sends no amount.
 */
export function ApproveWithAmount({
  a,
  type,
  label,
  onApprove,
  disabled,
  small,
  hideLine,
  icon,
}: {
  a: { start_date: string; end_date: string; typeId: number | null };
  type: ApiAbsenceType | undefined;
  label: string;
  onApprove: (expectedDays: number | undefined) => void;
  disabled?: boolean;
  small?: boolean;
  /** the amount is shown elsewhere on screen (the queue row, the drawer's Saldo og påvirkning), read from the same query */
  hideLine?: boolean;
  /** the check before the label, in the decision footer (#276) */
  icon?: boolean;
}) {
  const deductible = !!type?.is_deductible;
  const state = useDeductionState(
    { absence_type_id: a.typeId, start_date: a.start_date, end_date: a.end_date },
    deductible,
  );
  const timed = kindOf(type?.type) !== 'date';
  return (
    <>
      {!hideLine && <DeductionLine state={state} timed={timed} />}
      <Button
        small={small ?? false}
        // small is the queue's row button: a blue outline, as Innboks draws it. It was filled
        // AND given blue text, so "Innvilg" rendered as an empty blue bar (found 2026-09-21)
        {...(small
          ? { style: { borderColor: 'var(--act)', color: 'var(--act)' } }
          : { tone: 'pri' as const })}
        disabled={!!disabled || (deductible && state.status !== 'ready')}
        onClick={() => onApprove(deductible ? (state.days ?? undefined) : undefined)}
      >
        {icon && <Icon name="check" />}
        {label}
      </Button>
    </>
  );
}

/** What was already charged for an approved request, straight from the same endpoint. */
function ChargedLine({
  typeId,
  requestId,
  orig,
  type,
}: {
  typeId: number;
  requestId: number;
  orig: { start_date: string; end_date: string };
  type: ApiAbsenceType | undefined;
}) {
  // the charge stored on the absence when it was taken, not a recount under today's rule
  const state = useDeductionState(
    { absence_type_id: typeId, start_date: orig.start_date, end_date: orig.end_date, request_id: requestId },
    !!type?.is_deductible,
  );
  return <DeductionLine state={state} timed={kindOf(type?.type) !== 'date'} charged />;
}

/**
 * View, decision and edit are separate states (owner, 2026-09-21): an existing absence opens as
 * what it is and what it does to the balance and the rota, with the decision in the footer; the
 * fields appear only on Rediger, for somebody who may change it. A new absence opens on the fields.
 */
export function AbsenceDrawer({
  draft,
  shifts,
  onClose,
  docked = false,
}: {
  draft: AbsenceDraft | null;
  docked?: boolean;
  /** the shifts already on screen, for the "står oppsatt på N vakter" warning; not refetched here;
   * null while that list is unknown, which is said, never read as no shifts (#262) */
  shifts: ApiShift[] | null;
  onClose: () => void;
}) {
  const can = useCan();
  // Registering, changing and deleting an absence for somebody is absence.manage on the
  // server; a manager decides on requests and is shown the values, never fields
  // (middleware/rolePolicy.js, TF-SIMPLE-ROLES acceptance).
  const mayRegister = can('fravaer.registrere');
  const mayDecide = can('fravaer.godkjenne');
  const { tenant } = useTenant();
  const qc = useQueryClient();
  const usersQ = useUsers();
  const typesQ = useAbsenceTypes();
  const w = useAbsenceWrites();
  const sw = useShiftWrites();
  const [d, setD] = useState<AbsenceDraft | null>(draft);
  const [problem, setProblem] = useState('');
  const [editing, setEditing] = useState(false);
  // a decision waiting for its confirmation, with the reason typed so far (#276)
  const [ask, setAsk] = useState<{
    kind: 'approve' | 'reapprove' | 'decline' | 'withdraw';
    reason: string;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  // why a manager changed the request: the history's reason, not the employee's note (#276 FV-08)
  const [why, setWhy] = useState('');

  const users = useMemo(
    () =>
      (usersQ.data ?? [])
        .filter((u) => u.active_status)
        .sort((a, b) => fullName(a).localeCompare(fullName(b), 'nb')),
    [usersQ.data],
  );
  const types = useMemo(() => (typesQ.data ?? []).filter((t) => t.is_active), [typesQ.data]);
  const firstType = types[0]?.id;

  useEffect(() => {
    setD(draft);
    setProblem('');
    setEditing(false);
    setAsk(null);
    setWhy('');
  }, [draft]);
  // a changed request or registration is unsaved work: the shell's leave question, and the planner's
  // guard when another shift or absence is opened, ask before it goes (UI01-7)
  const changed = !!d && !!draft && JSON.stringify(d) !== JSON.stringify(draft);
  useDirty('absence-drawer', changed, T('Fravær'));
  // X, Escape and the backdrop ask too, docked and as a dialog (UI01-18): a changed absence was closed
  // by them without the question. Stay keeps the text; discard closes. A save or a decision closes
  // through onClose itself, not through here.
  const blockClose = () => {
    if (!changed) return false;
    askBeforeLeaving(T('Lukk fraværet'), onClose);
    return true;
  };

  // A draft that names no type falls back to the first active one, read rather than stored:
  // the types can arrive after the drawer opens, and nothing a caller holds has to know them.
  const typeId = d?.absence_type_id || firstType || 0;
  // an old absence can be on a type since deactivated; its view still names it
  const type = (typesQ.data ?? []).find((t) => t.id === typeId);
  const kind: Kind = kindOf(type?.type);
  // a grade per request: the Sykmeldingsgrad (ftrl § 8-13), or a part-time leave, delvis permisjon
  // (aml § 12-6), on a whole-day leave type (plan 0027 batch H)
  const graded = !!type?.is_sick_day || (type?.category === 'permisjon' && kind === 'date');
  const user = d ? users.find((u) => u.id === d.user_id) : undefined;
  const fields = !!d && mayRegister && (d.id == null || editing);
  /**
   * The person's published shifts inside the request. A full day is matched by date; a clock range
   * by instant, so a 10:00 to 12:00 request names the shift it cuts into and not every shift that day.
   */
  const overShifts = (x: AbsenceDraft): ApiShift[] | null => {
    if (!shifts) return null;
    const to = x.to || x.from;
    const iso = x.orig ?? undefined;
    if (kind === 'date')
      return shifts.filter(
        (s) => s.user_id === x.user_id && s.published && inRange(dOf(s.start_time), x.from, to),
      );
    if (!iso && !(x.from && x.fromT && x.toT)) return [];
    const s0 = iso?.start_date ?? osloISO(x.from, x.fromT);
    const e0 = iso?.end_date ?? osloISO(to, x.toT);
    return shifts.filter(
      (s) => s.user_id === x.user_id && s.published && s.start_time < e0 && s.end_time > s0,
    );
  };
  const over = d ? overShifts(d) : [];
  const nOver = over?.length ?? 0;
  /**
   * The type's limits against this person's other absences, from the server; warnings only, the
   * buttons stay as they are (owner, 2026-09-18). The stored range while it is untouched, else
   * what the fields say once they make a range.
   */
  const limitRange = (() => {
    if (!d || d.status === 'declined') return null;
    if (d.orig && sameRange(rangeOf(d.orig), d, kind)) return d.orig;
    const r = bodyOf(d, kind);
    return 'problem' in r ? null : r;
  })();
  const limitsQ = useAbsenceLimits(
    d?.user_id && limitRange && typeId
      ? { user_id: d.user_id, absence_type_id: typeId, ...limitRange, exclude_id: d.id }
      : null,
  );
  const limitWarnings = limitRange ? (limitsQ.data?.warnings ?? []) : [];
  // what approving takes, for the Saldo og påvirkning block; the same query the button holds on
  const pendingCharge = useDeductionState(
    d?.status === 'pending' && d.orig ? { absence_type_id: typeId, ...d.orig } : null,
    !!type?.is_deductible,
  );
  // the absence's history (ADR 0021), and whether the employee has asked to cancel it since it
  // was last approved: the newest of the two rows decides (backend cancel-request, 2026-09-21)
  const historyQ = useWorkTimeChanges({ absence_id: d?.id ?? undefined }, !!d?.id && can('timeliste.se'));
  const history = historyQ.data ?? [];
  const lastWord = history.find((h) =>
    ['absence_cancel_requested', 'absence_approved', 'absence_registered'].includes(h.action),
  );
  const cancelAsk =
    d?.status === 'accepted' && lastWord?.action === 'absence_cancel_requested' ? lastWord : null;

  const edit = (p: Partial<AbsenceDraft>) => {
    setProblem('');
    setD((x) => (x ? { ...x, ...p } : x));
  };

  /** Refusals stay in the drawer with what was typed; only a success closes it. */
  async function save(x: AbsenceDraft) {
    const range = bodyOf(x, kind);
    if ('problem' in range) {
      setProblem(range.problem);
      return;
    }
    const untouched = x.orig && sameRange(rangeOf(x.orig), x, kind);
    const grade = (x.grade ?? '').trim();
    // the 20 % floor is the sick grade's (ftrl § 8-13); an agreed part-time leave has none (aml § 12-6, #227)
    const floor = type?.is_sick_day ? 20 : 1;
    if (
      graded &&
      grade &&
      !(Number.isInteger(Number(grade)) && Number(grade) >= floor && Number(grade) <= 100)
    ) {
      setProblem(
        type?.is_sick_day
          ? T('Sykmeldingsgraden må være et helt tall fra 20 til 100, eller stå tom.')
          : T('Permisjonsgraden må være et helt tall fra 1 til 100, eller stå tom.'),
      );
      return;
    }
    const body = {
      user_id: x.user_id,
      absence_type_id: typeId,
      start_date: untouched && x.orig ? x.orig.start_date : range.start_date,
      end_date: untouched && x.orig ? x.orig.end_date : range.end_date,
      reason: x.reason || null,
      ...(graded ? { sick_grade: grade ? Number(grade) : null } : {}),
      ...(x.id != null && why.trim() ? { change_reason: why.trim() } : {}),
    };
    try {
      if (x.id == null) {
        // One call. POST /admin/absence-requests writes status 'accepted' and takes the days
        // itself (absenceRequestController.js:508-527), so the approve that used to follow it
        // was always refused with "already been accepted": the absence existed and the balance
        // was charged, and the drawer stayed open showing that refusal (found 2026-09-18).
        await w.create.mutateAsync(body);
        toast(T('{name} registrert', { name: type?.name ?? T('Fravær') }));
      } else {
        await w.update.mutateAsync({ id: x.id, body });
        toast(T('Lagret'));
      }
      onClose();
    } catch (e) {
      setProblem(explain(e));
      // the Registrer button was disabled while it ran and focus fell to <body>: put it on the reason (#272 B-28)
      setTimeout(() => document.querySelector<HTMLElement>('[data-testid=absence-problem]')?.focus());
    }
  }
  const freeShifts = async (x: AbsenceDraft) => {
    const list = overShifts(x) ?? [];
    for (const s of list) await sw.update.mutateAsync({ id: s.id, body: { user_id: null } });
    toast(T('{n} vakter gjort ledige', { n: list.length }));
  };

  const to = d ? d.to || d.from : '';
  const dayCount = kind === 'date' && d?.from && to >= d.from ? daysOf(d.from, to) : null;
  const dagerText = (n: number) => `${n} ${n === 1 ? T('dag') : T('dager')}`;
  // the balance the charge comes out of: this year's field, or next year's for an absence then
  const balances = user as
    | { vacation_days_balance?: number | null; next_year_vacation_days_balance?: number | null }
    | undefined;
  const thisYear = new Date().getFullYear();
  const balanceYear = d && d.from.slice(0, 4) === to.slice(0, 4) ? Number(d.from.slice(0, 4)) : null;
  // where the person stands on vacation, for whoever decides (owner 2026-10-25): the figures come from
  // GET /admin/absence-standing; an absence over new year keeps the server's deduction sentence
  const standingQ = useAbsenceStanding(
    d?.user_id,
    balanceYear === thisYear || balanceYear === thisYear + 1 ? balanceYear : null,
    !!type?.is_deductible && d?.id != null && d?.status !== 'declined',
  );
  const standing = standingQ.data?.vacation;
  const pending = d?.status === 'pending';
  const viewing = !!d && d.id != null && !fields;
  // a pending request's balance after approving it alone, the approve route's own sum (assertBalanceCovers)
  const after = pending && standing && pendingCharge.days != null ? standing.left - pendingCharge.days : null;
  const unknownBalance =
    pending &&
    !!type?.is_deductible &&
    balanceYear != null &&
    (standingQ.isError || pendingCharge.status === 'error');
  const blocked = after != null && after < 0;
  const gone = type?.is_active === false;
  // the person, also a former employee, whose request still names them (FV-07)
  const person = (usersQ.data ?? []).find((u) => u.id === d?.user_id);
  const personName = person ? fullName(person) : '';
  const team = teamsOf(person, tenant?.id)
    .map((t) => t.name)
    .join(', ');
  const former = !!person && !person.active_status;
  const amountText =
    dayCount != null
      ? dagerText(dayCount)
      : d?.orig
        ? `${num((Date.parse(d.orig.end_date) - Date.parse(d.orig.start_date)) / 36e5, 1)} ${T('timer')}`
        : '';
  const periodLabel = d ? (kind === 'date' ? periodText(d.from, to) : describe(d, kind)) : '';

  /**
   * A decision, after its confirmation: one write, the drawer closes on success. A refusal stays in the
   * drawer with the confirmation and the reason typed (#276 FV-16); a 409 on the amount reads the
   * request again. The button is held while it runs, so a double click sends one write.
   */
  async function decide(label: string, f: () => Promise<unknown>) {
    setBusy(true);
    setProblem('');
    try {
      await f();
      toast(label);
      setAsk(null);
      onClose();
    } catch (e) {
      const days =
        e instanceof ApiError && e.status === 409
          ? (e.body as { days?: number } | undefined)?.days
          : undefined;
      if (days !== undefined) void invalidateAbsences(qc);
      setProblem(
        days !== undefined
          ? T('Trekket er endret siden du så det: {count} dag(er). Se over og godkjenn på nytt.', {
              count: days,
            })
          : errorText(e),
      );
    } finally {
      setBusy(false);
    }
  }
  const confirmed = () => {
    if (!d?.id || !ask) return;
    const id = d.id;
    if (ask.kind === 'approve')
      return decide(T('Innvilget'), () =>
        w.approve.mutateAsync({
          id,
          expected_days: type?.is_deductible ? (pendingCharge.days ?? undefined) : undefined,
        }),
      );
    if (ask.kind === 'reapprove') return decide(T('Innvilget'), () => w.approve.mutateAsync(id));
    if (ask.kind === 'decline')
      return decide(T('Avslått'), () =>
        w.decline.mutateAsync({ id, expected_status: 'pending', change_reason: ask.reason }),
      );
    return decide(T('Trukket tilbake, dagene er lagt tilbake på saldoen'), () =>
      w.decline.mutateAsync({ id, expected_status: 'accepted', change_reason: ask.reason }),
    );
  };

  const head = viewing
    ? {
        lead: (
          <span className={`av ${avTone(d.user_id)} ad-av`} aria-hidden="true">
            {initials(personName)}
          </span>
        ),
        title: personName || T('Fravær'),
        sub: (
          <span className="ad-meta">
            {(team || former) && (
              <span>{[team, former ? T('Tidligere ansatt') : ''].filter(Boolean).join(', ')}</span>
            )}
            <StatusChip status={d.status} cancel={!!cancelAsk} />
          </span>
        ),
      }
    : {
        lead: undefined,
        title: d?.id == null ? T('Registrer fravær') : T('Rediger fravær'),
        sub: d
          ? `${user ? `${fullName(user)}, ` : ''}${describe(d, kind)}${dayCount != null ? `, ${dagerText(dayCount)}` : ''}`
          : '',
      };

  const askTitle =
    ask?.kind === 'approve'
      ? T('Innvilge søknaden?')
      : ask?.kind === 'reapprove'
        ? T('Innvilge likevel?')
        : ask?.kind === 'decline'
          ? T('Avslå søknaden?')
          : cancelAsk
            ? T('Avlyse fraværet?')
            : T('Trekke tilbake fraværet?');
  const askButton =
    ask?.kind === 'approve' || ask?.kind === 'reapprove'
      ? T('Innvilg søknad')
      : ask?.kind === 'decline'
        ? T('Avslå søknad')
        : cancelAsk
          ? T('Avlys fraværet')
          : T('Trekk tilbake');

  return (
    <Drawer
      docked={docked}
      open={!!draft}
      onClose={onClose}
      blockClose={blockClose}
      className={viewing ? 'adetail' : undefined}
      lead={head.lead}
      title={head.title}
      sub={head.sub}
      footer={
        d &&
        (d.id == null ? (
          <>
            <span className="sp" />
            <Button tone="pri" onClick={() => save(d)} disabled={!typeId || !d.user_id || w.create.isPending}>
              {T('Registrer')}
            </Button>
          </>
        ) : fields ? (
          <>
            <Button
              ghost
              tone="dgr"
              onClick={() => decide(T('Fravær slettet'), () => w.remove.mutateAsync(d.id as number))}
            >
              <Icon name="trash" />
              {T('Slett')}
            </Button>
            <span className="sp" />
            <Button
              onClick={() => {
                setEditing(false);
                setWhy('');
                setD(draft);
              }}
            >
              {T('Avbryt')}
            </Button>
            <Button tone="pri" onClick={() => save(d)} disabled={w.update.isPending}>
              {T('Lagre')}
            </Button>
          </>
        ) : ask ? (
          <>
            <span className="sp" />
            <Button onClick={() => setAsk(null)} disabled={busy}>
              {T('Avbryt')}
            </Button>
            <Button
              tone={ask.kind === 'decline' ? 'dgr' : 'pri'}
              onClick={confirmed}
              disabled={busy}
              aria-busy={busy}
            >
              {askButton}
            </Button>
          </>
        ) : (
          <>
            {mayRegister && (
              <Button ghost onClick={() => setEditing(true)}>
                <Icon name="pencil" />
                {T('Rediger')}
              </Button>
            )}
            <span className="sp" />
            {!mayDecide ? null : d.status === 'pending' ? (
              <>
                <Button ghost tone="dgr" onClick={() => setAsk({ kind: 'decline', reason: '' })}>
                  {T('Avslå')}
                </Button>
                {/* no Innvilg where the server would refuse it (a deleted type, a balance below 0, an unknown
                    balance): the notice above says why, and what to do (owner rule: no control that cannot work) */}
                {!gone && !blocked && !unknownBalance && d.orig && (
                  <ApproveWithAmount
                    a={{ ...d.orig, typeId }}
                    type={type}
                    label={T('Innvilg søknad')}
                    icon
                    hideLine
                    onApprove={() => setAsk({ kind: 'approve', reason: '' })}
                  />
                )}
              </>
            ) : d.status === 'accepted' ? (
              <Button
                {...(cancelAsk ? { tone: 'pri' as const } : {})}
                onClick={() => setAsk({ kind: 'withdraw', reason: '' })}
              >
                {cancelAsk ? T('Avlys fraværet') : T('Trekk tilbake')}
              </Button>
            ) : (
              <Button tone="pri" onClick={() => setAsk({ kind: 'reapprove', reason: '' })}>
                {T('Innvilg likevel')}
              </Button>
            )}
          </>
        ))
      }
    >
      {d && (
        <>
          {problem && (
            <div className="al dgr" role="alert" data-testid="absence-problem" tabIndex={-1}>
              <span>
                <Icon name="stop" />
              </span>
              <span>{problem}</span>
            </div>
          )}
          {viewing && ask && (
            <section className="ad-ask" data-testid="absence-confirm" aria-labelledby="ad-ask-h">
              <h4 id="ad-ask-h">{askTitle}</h4>
              <p>
                <b>{personName}</b>
                <br />
                {periodLabel}
                {amountText ? `, ${amountText}` : ''}
              </p>
              <p>
                {ask.kind === 'approve'
                  ? type?.is_deductible && standing && pendingCharge.days != null
                    ? T('Tilgjengelig saldo endres fra {now} til {left} dager.', {
                        now: num(standing.left, 1),
                        left: num(standing.left - pendingCharge.days, 1),
                      })
                    : type?.is_deductible && pendingCharge.days != null
                      ? deductionLine(pendingCharge.days)
                      : T('Feriesaldoen endres ikke.')
                  : ask.kind === 'decline'
                    ? T('Saldoen endres ikke.')
                    : ask.kind === 'withdraw'
                      ? T(
                          'Fraværet fjernes fra innvilget fravær, og trukne feriedager legges tilbake på saldoen.',
                        )
                      : T('Fraværet innvilges, og feriedager trekkes etter bedriftens regel.')}
                {ask.kind === 'decline'
                  ? ''
                  : over == null
                    ? ` ${T('Vaktplanen er ikke hentet, så berørte vakter er ikke sjekket.')}`
                    : nOver > 0
                      ? ` ${nOver === 1 ? T('1 vakt må følges opp separat.') : T('{n} vakter må følges opp separat.', { n: nOver })}`
                      : ''}{' '}
                {T('Den ansatte varsles som vanlig.')}
              </p>
              {(ask.kind === 'decline' || ask.kind === 'withdraw') && (
                <label className="fld">
                  <span className="lbl">{T('Begrunnelse, valgfri')}</span>
                  <textarea
                    className="inp w"
                    rows={3}
                    value={ask.reason}
                    onChange={(e) => setAsk({ ...ask, reason: e.target.value })}
                  />
                  <small className="hint">
                    {T('Lagres i historikken og sendes med beskjeden til den ansatte.')}
                  </small>
                </label>
              )}
            </section>
          )}
          {viewing && !ask && (
            <section className="ad-sum" data-testid="absence-summary">
              <div className="ad-kind">
                <h4>
                  {type?.name ?? T('Fravær')}
                  {gone && <span className="ad-gone">{T('Utgått')}</span>}
                </h4>
                {amountText && <span className="ad-amt">{amountText}</span>}
              </div>
              <p className="ad-per">
                <Icon name="calendar" />
                <span>{periodLabel}</span>
              </p>
              {d.created && (
                <p className="ad-rec">
                  {pending ? T('Mottatt') : T('Opprettet')} {stampText(oslo(d.created))}
                  {former ? `. ${T('Søknaden tilhører en tidligere ansatt.')}` : ''}
                </p>
              )}
              <dl className="ad-note">
                <dt>
                  <Icon name="chat" />
                  <span>{pending ? T('Merknad fra ansatt') : T('Merknad')}</span>
                </dt>
                <dd>{d.reason || T('Ingen merknad lagt til.')}</dd>
              </dl>
            </section>
          )}
          {viewing && !ask && cancelAsk && (
            <div className="al warn" data-testid="absence-cancel-asked">
              <span>
                <Icon name="warn" />
              </span>
              <span>
                <b>{T('Den ansatte ber om å avlyse fraværet.')}</b>{' '}
                {cancelAsk.reason ? `«${cancelAsk.reason}» ` : ''}
                {T('Avlys det, så legges dagene tilbake på saldoen, eller la det stå.')}
              </span>
            </div>
          )}
          {viewing && !ask && pending && gone && (
            <div className="al warn" data-testid="absence-type-gone">
              <span>
                <Icon name="warn" />
              </span>
              <span>
                <b>{T('Fraværstypen er slettet.')}</b> {T('Bytt type under Rediger, eller avslå.')}
              </span>
            </div>
          )}
          {viewing && !ask && unknownBalance && (
            <div className="al warn" data-testid="absence-balance-unknown">
              <span>
                <Icon name="warn" />
              </span>
              <span>
                <b>{T('Saldo må avklares.')}</b>{' '}
                {T('Saldoen eller trekket kunne ikke hentes, så søknaden kan ikke innvilges ennå.')}{' '}
                <button
                  type="button"
                  className="mlink"
                  onClick={() => {
                    void standingQ.refetch();
                    pendingCharge.refetch();
                  }}
                >
                  {T('Prøv igjen')}
                </button>
              </span>
            </div>
          )}
          {viewing && !ask && blocked && after != null && (
            <div className="al warn" data-testid="absence-blocked">
              <span>
                <Icon name="warn" />
              </span>
              <span>
                <b>{T('Kan ikke innvilges med saldoen som er.')}</b>{' '}
                {T(
                  'Saldo etter innvilgelse blir {left} dager, og den kan ikke gå under 0. Korriger søknaden under Rediger, eller avslå.',
                  { left: num(after, 1) },
                )}
              </span>
            </div>
          )}
          {!ask && type && ownVacationWithoutDeduction(type) && d.status !== 'declined' && (
            <div className="al warn" data-testid="absence-own-vacation">
              <span>
                <Icon name="warn" />
              </span>
              <span>
                {T(
                  '«{name}» er bedriftens egen fraværstype og trekker ikke feriedager: saldoen endres ikke. Det står under Innstillinger → Vakt- og fraværstyper.',
                  { name: type.name },
                )}
              </span>
            </div>
          )}
          {!ask && limitWarnings.length > 0 && (
            <div
              className="al warn"
              data-testid="absence-limits"
              title={limitWarnings.map((w) => w.text).join(' ')}
            >
              <span>
                <Icon name="warn" />
              </span>
              <span>
                {/* the first sentence of each; the law and the exceptions are the box's tooltip, and the button
                    already says it can be approved (owner 2026-10-25 "too much text") */}
                <b>{T('Over en grense for {name}.', { name: type?.name ?? '' })}</b>{' '}
                {limitWarnings.map((w) => w.text.match(/^.*?[.!?](?=\s|$)/)?.[0] ?? w.text).join(' ')}
              </span>
            </div>
          )}
          {viewing && !ask && d.status !== 'declined' && type?.is_deductible && (
            <section className="ad-bal" data-testid="absence-impact">
              <h4 className="ad-h">
                <Icon name="calendar" />
                <span>{T('Feriesaldo')}</span>
                {balanceYear && <small>{balanceYear}</small>}
              </h4>
              {pending && balanceYear != null && (standing || unknownBalance) ? (
                <>
                  <div className="ad-fig" data-testid="absence-standing">
                    <div>
                      <span>{T('Tilgjengelig nå')}</span>
                      <strong>
                        {standing ? num(standing.left, 1).replace('-', '−') : T('Ukjent')}
                        {standing && <small> d</small>}
                      </strong>
                    </div>
                    <div>
                      <span>{T('Omsøkt trekk')}</span>
                      <strong>
                        {pendingCharge.days != null ? `−${num(pendingCharge.days, 1)}` : T('Ukjent')}
                        {pendingCharge.days != null && <small> d</small>}
                      </strong>
                    </div>
                    <div className={`ad-res${after == null ? ' unk' : after < 0 ? ' neg' : ''}`}>
                      <span>{T('Etter innvilgelse')}</span>
                      <strong>
                        {after != null ? num(after, 1).replace('-', '−') : T('Ukjent')}
                        {after != null && <small> d</small>}
                      </strong>
                    </div>
                  </div>
                  <p className="ad-exp">{T('Trekket skjer bare hvis søknaden innvilges.')}</p>
                </>
              ) : pending ? (
                <DeductionLine state={pendingCharge} timed={kind !== 'date'} />
              ) : (
                <>
                  {d.orig && (
                    <ChargedLine typeId={typeId} requestId={d.id as number} orig={d.orig} type={type} />
                  )}
                  {standing && (
                    <p className="ad-set">
                      <span>{T('Tilgjengelig nå')}</span>
                      <strong>
                        {num(standing.left, 1).replace('-', '−')} <small>{T('dager')}</small>
                      </strong>
                    </p>
                  )}
                </>
              )}
              {standing && (
                <details className="ad-dis">
                  <summary>
                    <span>{T('Vis saldogrunnlag')}</span>
                    <Icon name="chevronRight" />
                  </summary>
                  <QuotaRow
                    label={T('Feriedager igjen {year}', { year: standing.year })}
                    down
                    limit={standing.limit}
                    parts={[
                      { kind: 'used', n: standing.used, text: T('brukt') },
                      { kind: 'ok', n: standing.approved, text: T('innvilget') },
                      { kind: 'this', n: pending ? (pendingCharge.days ?? 0) : 0, text: T('dette fraværet') },
                    ]}
                  />
                  <dl className="ad-eq">
                    <dt>{T('Årets dager')}</dt>
                    <dd>
                      {num(standing.limit, 1)}
                      {standing.carried && standing.entitlement
                        ? ` (${T('{n} + {c} overført', { n: standing.entitlement, c: standing.carried })})`
                        : ''}
                    </dd>
                    <dt>{T('Brukt')}</dt>
                    <dd>−{num(standing.used, 1)}</dd>
                    <dt>{T('Innvilget fremover')}</dt>
                    <dd>−{num(standing.approved, 1)}</dd>
                    <dt className="tot">{T('Tilgjengelig nå')}</dt>
                    <dd className="tot">{num(standing.left, 1).replace('-', '−')}</dd>
                  </dl>
                </details>
              )}
            </section>
          )}
          {viewing && !ask && d.status !== 'declined' && (
            <section className="ad-staff">
              <h4 className="ad-h">
                <Icon name="users" />
                <span>{T('Påvirkning på vaktplan')}</span>
              </h4>
              {over == null ? (
                <p>{T('Vaktplanen er ikke hentet, så berørte vakter er ikke sjekket.')}</p>
              ) : over.length > 0 ? (
                <>
                  <details className="ad-dis">
                    <summary>
                      <span>
                        {over.length === 1
                          ? T('1 planlagt vakt berøres')
                          : T('{n} planlagte vakter berøres', { n: over.length })}
                      </span>
                      <Icon name="chevronRight" />
                    </summary>
                    <ul className="ad-shifts">
                      {over.map((s) => (
                        <li key={s.id}>
                          <span>{dayText(dOf(s.start_time))}</span>
                          <span>
                            {oslo(s.start_time)?.time}–{oslo(s.end_time)?.time}
                          </span>
                        </li>
                      ))}
                    </ul>
                    {/* what happens otherwise is the button's tooltip, not a sentence (owner 2026-10-25 "too much text") */}
                    <Button
                      onClick={() => freeShifts(d)}
                      title={T('Innvilges fraværet uten å ta stilling, blir vaktene stående.')}
                    >
                      {T('Gjør vaktene ledige')}
                    </Button>
                  </details>
                  <p>{T('Vaktene blir ikke flyttet automatisk ved innvilgelse.')}</p>
                </>
              ) : (
                <p>{T('Ingen planlagte vakter i perioden')}</p>
              )}
            </section>
          )}
          {fields && (
            <>
              <div className="fld">
                <span className="lbl">{T('Ansatt')}</span>
                <select
                  className="inp w"
                  aria-label={T('Ansatt')}
                  value={d.user_id}
                  onChange={(e) => edit({ user_id: Number(e.target.value) })}
                  disabled={d.id != null}
                >
                  {/* Registrer fravær names nobody until the manager picks the person (#272 B-28, M-22) */}
                  {!d.user_id && (
                    <option value={0} disabled>
                      {T('Velg ansatt')}
                    </option>
                  )}
                  {/* a former employee's request still names them; the select never shows somebody else (FV-07) */}
                  {d.user_id > 0 && !users.some((u) => u.id === d.user_id) && (
                    <option value={d.user_id}>{personName || T('Tidligere ansatt')}</option>
                  )}
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {fullName(u)}
                    </option>
                  ))}
                </select>
              </div>
              {typesQ.isSuccess && types.length === 0 && (
                <div className="al info" data-testid="absence-no-types">
                  <span>ⓘ</span>
                  <span>
                    {T('Bedriften har ingen fraværstyper ennå; uten en type kan fravær ikke registreres.')}{' '}
                    <Link to="/innstillinger/typer">{T('Legg inn fraværstyper under Innstillinger.')}</Link>
                  </span>
                </div>
              )}
              <div className="fld">
                <span className="lbl">{T('Type')}</span>
                <select
                  className="inp w"
                  aria-label={T('Type')}
                  disabled={types.length === 0}
                  value={typeId}
                  onChange={(e) => edit({ absence_type_id: Number(e.target.value) })}
                >
                  {/* a type deleted since stays named, not replaced by the first active one: the manager picks
                      another on purpose (FV-07); the server refuses to approve it as it is */}
                  {gone && type && (
                    <option value={type.id} disabled>
                      {T('{name} (utgått)', { name: type.name })}
                    </option>
                  )}
                  {types.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name}
                    </option>
                  ))}
                </select>
              </div>
              <RangeFields kind={kind} value={d} onChange={(r) => edit(r)} />
              {graded && !type?.is_sick_day && (
                <div className="fld">
                  <span className="lbl">{T('Permisjonsgrad, prosent')}</span>
                  <input
                    className="inp"
                    style={{ width: 90 }}
                    inputMode="numeric"
                    aria-label={T('Permisjonsgrad, prosent')}
                    placeholder="100"
                    value={d.grade ?? ''}
                    onChange={(e) => edit({ grade: e.target.value })}
                  />
                  <small className="hint">
                    {T(
                      'Tom ved hel permisjon. Ved delvis permisjon (arbeidsmiljøloven § 12-6) teller graden av hver dag som fravær; timene personen jobber betales som vanlig.',
                    )}
                  </small>
                </div>
              )}
              {type?.is_sick_day && (
                <div className="fld">
                  <span className="lbl">{T('Sykmeldingsgrad, prosent')}</span>
                  <input
                    className="inp"
                    style={{ width: 90 }}
                    inputMode="numeric"
                    aria-label={T('Sykmeldingsgrad, prosent')}
                    placeholder="100"
                    value={d.grade ?? ''}
                    onChange={(e) => edit({ grade: e.target.value })}
                  />
                  <small className="hint">
                    {T(
                      'Tom ved hel sykmelding. Ved gradert sykmelding betales graden av hver planlagt dag som sykepenger; timene personen jobber betales som vanlig.',
                    )}
                  </small>
                </div>
              )}
              <div className="fld">
                <span className="lbl">{T('Merknad')}</span>
                <input
                  className="inp w"
                  aria-label={T('Merknad')}
                  value={d.reason}
                  onChange={(e) => edit({ reason: e.target.value })}
                />
              </div>
              {d.id != null && (
                <div className="fld">
                  <span className="lbl">{T('Begrunnelse for endringen, valgfri')}</span>
                  <input
                    className="inp w"
                    aria-label={T('Begrunnelse for endringen, valgfri')}
                    value={why}
                    onChange={(e) => setWhy(e.target.value)}
                  />
                  <small className="hint">
                    {T('Lagres i historikken med ditt navn. Søknaden får samme status som før.')}
                  </small>
                </div>
              )}
              {d.status === 'accepted' && d.orig && d.id != null && (
                <ChargedLine typeId={typeId} requestId={d.id} orig={d.orig} type={type} />
              )}
            </>
          )}
          {type && !ask && (
            <div className="ad-more">
              <details className="ad-dis">
                <summary>
                  <Icon name="document" />
                  <span>
                    {viewing ? T('Regler og lønnsgrunnlag') : T('Regler for {name}', { name: type.name })}
                  </span>
                  <Icon name="chevronRight" />
                </summary>
                <ul className="vlist">
                  {[
                    type.is_deductible
                      ? // the remaining days are the administrator's field: say the rule, and the
                        // number only to a reader who is sent it
                        `${T('Trekker fra feriedager.')}${
                          balances?.vacation_days_balance != null
                            ? ` ${T('{n} igjen.', { n: String(balances.vacation_days_balance) })}`
                            : ''
                        }`
                      : T('Trekker ikke fra feriesaldoen.'),
                    `${payerText(type)}.`,
                    limitsText(type).length
                      ? T('Grenser: {list}.', { list: limitsText(type).join(', ') })
                      : '',
                    lawNote(type) ? `${lawNote(type)}.` : '',
                    // the pay export is the administrator's screen, so its rule is their line too
                    !can('ansatte.loenn')
                      ? ''
                      : type.salary_type_code
                        ? type.absence_rate && Number(type.absence_rate) !== 1
                          ? T('Leveres som lønnsart {code}, sats {rate} %.', {
                              code: type.salary_type_code,
                              rate: num(Number(type.absence_rate) * 100, 0),
                            })
                          : T('Leveres som lønnsart {code}.', { code: type.salary_type_code })
                        : T('Ingen lønnsart satt: kommer ikke med i eksporten.'),
                    T('Sluttdatoen er en fraværsdag.'),
                  ]
                    .filter(Boolean)
                    .map((x, i) => (
                      <li key={x}>
                        <span className="vn">{i + 1}</span>
                        <span>{x}</span>
                      </li>
                    ))}
                </ul>
              </details>
              {history.length > 0 && (
                <details className="ad-dis">
                  <summary>
                    <Icon name="clock" />
                    <span>{T('Historikk')}</span>
                    <span className="ad-cnt">{history.length}</span>
                    <Icon name="chevronRight" />
                  </summary>
                  <History rows={history} />
                </details>
              )}
            </div>
          )}
        </>
      )}
    </Drawer>
  );
}
